/**
 * 过程 trace 的纯逻辑。
 *
 * 目标形态（对齐 mateclaw 新版）：一次运行里的"过程"（思考、工具调用、过程叙述）
 * 收成一条可开合的列表，每行是「图标 + 名称 + 可选徽标 + ·分隔的详情段 + 右侧箭头」，
 * 正式结论渲染在这条 trace 之外。
 *
 * 本文件只做数据 → 行的转换，不碰 DOM、不碰 store，便于单测。
 *
 * ★ 标记为「猜测」的地方是截图看不出来的语义，集中放在这里，便于按反馈调整；
 *   结构（trace / 行 / 表头）不依赖这些猜测。
 */

import { getToolDisplayName, toolNameOf } from './toolNames'
import { resolveCommandBrand, BROWSER_TOOL_BRAND } from './toolIconResolver'

/** 过程说明的协议标记。 */
export const PROCESS_MARKER = '【分析过程】'

/** 单行的详情段最多渲染多少段（超出交给 CSS 省略号）。 */
const MAX_SEGMENTS = 4

/**
 * 正文长度阈值：工具后的无标记叙述，内容超过这个字数即判定为"最终正文"。
 * 取 100：过程叙述通常是一两句话（<100 字），报告类正文很快超过。
 * 过小会误把长叙述当正文（漏出气泡）；过大会让正文多隐藏一阵才进气泡。可按实际数据调。
 */
const BODY_LEN_THRESHOLD = 100

/**
 * 这段文字是否已经"像最终正文"（用于把缓冲中的工具后叙述落定为正文）。
 *
 * 刻意只用**强信号**，避免误判过程叙述：
 *   - 字数超过 BODY_LEN_THRESHOLD（报告类正文很快超）；或
 *   - 出现 markdown 强结构：ATX 标题（## 等）、表格行（|）、代码块（```）。
 *     过程叙述几乎不会出现这些；连续空行 / 列表符号太容易和叙述混，所以不用。
 *
 * 只在"看起来像正文"时返回 true；不像时返回 false，让叙述继续隐藏等下一条工具调用。
 */
export function looksLikeBody(content) {
  if (typeof content !== 'string' || !content) return false
  if (content.length > BODY_LEN_THRESHOLD) return true
  if (/^\s*#{1,6}\s/m.test(content)) return true
  if (/^\s*\|/m.test(content)) return true
  if (/```/.test(content)) return true
  return false
}

/**
 * 剥掉行首的【分析过程】标记。
 *
 * 只在"标记位于行首（前面只有空白）"时才剥，避免误伤正在讨论这个标记的正文。
 * 无标记时原样返回（不产生任何改动）。
 */
export function stripProcessMarker(text) {
  if (!text || typeof text !== 'string') return text
  if (text.indexOf(PROCESS_MARKER) === -1) return text
  return text
    .split('\n')
    .map((line) => {
      const idx = line.indexOf(PROCESS_MARKER)
      if (idx === -1) return line
      if (line.slice(0, idx).trim() !== '') return line
      return line.slice(0, idx) + line.slice(idx + PROCESS_MARKER.length).replace(/^[ \t]+/, '')
    })
    .join('\n')
}

/**
 * 这段文字是否**自报家门**说"我是过程"。只看开头，不看中间。
 *
 * 【分析过程】是模型侧的协议：动手之前先声明"接下来这段是过程叙述"。
 * 它的真正价值在于**到得早** —— 第一段流式文字就带着它，
 * 于是前端不必等下一条消息（工具调用）到达才知道这段该进容器。
 * 而"等一下条消息"正是"文字先闪在容器外、再跳进容器"的根因。
 */
export function hasProcessMarker(text) {
  if (!text || typeof text !== 'string') return false
  return text.replace(/^[\s\u3000]+/, '').indexOf(PROCESS_MARKER) === 0
}

/**
 * "运行中、且应**立刻**按过程渲染"的那条消息的 id（没有则 null）。
 *
 * 规则（模型协议）：本轮最后一条 + 运行中 + 开头带【分析过程】标记。
 * 标记是模型侧的契约："动手之前先声明接下来这段是过程叙述"，它到得早 ——
 * 第一段流式文字就带着，于是不必等下一条消息（工具调用）确认。
 *
 * 曾经还有一条"规则二"：运行中 + 本轮出现过工具调用 → 最后一条叙述一律先按
 * 过程渲染。它确实消灭了"工具间叙述先闪气泡、再跳进容器"的闪动，但代价是
 * **最终正文**（同样是工具后的无标记叙述）会先在容器里流式，直到运行结束才被
 * 反向扫描送回气泡 —— 观感就是"正文先在工具卡片里输出、结束瞬间一下子回显到
 * 页面上"。规则二已移除，改由消息列表组件做**缓冲判定**（见
 * findLiveUnmarkedNarration）：无标记的工具后叙述先缓冲一小段窗口，窗口内来了
 * 下一条工具调用 → 判定为过程叙述，进容器（从未在容器外出现过，不闪）；窗口
 * 耗尽仍无工具调用 → 先按最终正文在气泡里流式输出。
 *
 * ★落锁只决定"是否继续隐藏等待"，不决定消息的分组归属：分组权威始终是
 *   computeProcessIds 反向扫描。若该叙述此后真的跟来了工具调用（只是缓冲窗口
 *   先耗尽），它仍会被反向扫描收进同一条 trace —— 宁可"气泡→容器"跳一次，也不
 *   让一轮裂成多条 trace，从而做到实时渲染与刷新后的历史渲染形态一致。
 */
export function pendingProcessId(messages, isLive) {
  if (!isLive || !Array.isArray(messages) || !messages.length) return null
  const last = messages[messages.length - 1]
  if (!last || last.type !== 'assistant') return null
  if (last.id == null) return null
  // 自报家门：带【分析过程】标记 → 立即按过程渲染
  if (hasProcessMarker(last.content)) return last.id
  return null
}

/**
 * 运行中、需要做"缓冲判定"的那条最后叙述（无标记、且本轮已动过手），没有则 null。
 *
 * 背景：工具之间的过程叙述与最终正文，在流式第一帧的结构完全相同 —— 都是
 * "工具之后的无标记 assistant 文本"，前端无法预知后面还会不会来下一条工具调用。
 * 于是对这类叙述不做即时预判（老规则二的教训），而是交给组件缓冲一个短窗口：
 *
 *   - 窗口内来了下一条工具调用 → 它是过程叙述（反向扫描也会这么判）→ 进容器；
 *     因为缓冲期间它从未渲染到容器外，所以不会闪；
 *   - 窗口耗尽仍无工具调用 → 先按最终正文在气泡里流式输出；此后若又跟来工具
 *     调用，仍由反向扫描收进同一条 trace（落锁不改变分组归属）。
 *
 * 缓冲期间该消息既不进气泡也不进容器（组件在分组时跳过），正文最多延迟约一个
 * 窗口的时长出现在气泡里。
 *
 * 判定范围（刻意保守）：
 *   - 只认"最后一条消息"（运行中尚未落定的那段输出）；落定后的归属全交给
 *     computeProcessIds 反向扫描；
 *   - 带【分析过程】标记的不在此列（走 pendingProcessId 立即进容器）；
 *   - MEDIA: 媒体消息、错误消息不缓冲（它们本就该直接按普通消息展示）；
 *   - 向前跳过连续叙述与思考，看最近一条其余消息：是工具调用 → 本轮已动手，
 *     需要缓冲判定；是 user / system → 本轮没动过手（普通问答），回答必须从
 *     第一帧就是气泡，绝不缓冲。
 *
 * ★刻意**不要求 msg.isStreaming**：叙述流完之后、下一条消息到达之前，存在
 * "isStreaming 已为 false、但回合还没结束"的间隙，判定身份不能在间隙里丢。
 */
export function findLiveUnmarkedNarration(messages) {
  if (!Array.isArray(messages) || !messages.length) return null
  const last = messages[messages.length - 1]
  if (!last || last.type !== 'assistant' || last.id == null) return null
  if (last.isError) return null
  const content = typeof last.content === 'string' ? last.content : ''
  // 带标记的走 pendingProcessId 立即进容器；媒体消息直接按普通消息展示
  if (hasProcessMarker(content)) return null
  if (content.indexOf('MEDIA:') === 0) return null
  // 向前跳过连续叙述与思考（同一段输出的分段 / 工具后的推理），看最近一条
  // 其余消息：是工具调用 → 本轮已动手；是 user / system → 本轮没动过手。
  for (let i = messages.length - 2; i >= 0; i--) {
    const m = messages[i]
    if (!m) continue
    if (m.type === 'assistant' || m.type === 'thinking') continue
    if (m.type === 'toolCall' || m.type === 'tool') return last
    return null
  }
  return null
}

/**
 * 过程消息 id 集合。
 *
 * 判定做的是**反向扫描**：一条叙述是不是过程，取决于它后面还有没有过程内容。
 *
 * 为什么必须反向 —— 截图里第 7、8 行是两条**连续的叙述**
 * （"好了，数据已经全部齐备…" 紧跟 "数据已完整闭合…"）。
 * 若按"后面紧跟工具调用"的正向判定，前一条后面跟的是叙述而不是工具，
 * 就会被误判成正式结论、跑到 trace 外面去。反向扫描天然处理连续叙述：
 * 只要后面还有过程内容，前面的叙述就都属于过程。
 *
 * 规则：
 *   - 思考、工具调用           → 永远是过程
 *   - assistant 叙述           → 当且仅当"紧跟其后的一条是过程"才算过程
 *   - 用户/系统消息            → 不是过程，且会打断传播
 * 于是"本轮最后一条、后面什么都没有"的 assistant 就是正式结论，天然落在 trace 之外。
 *
 * 纯派生、只读，不修改任何消息对象。
 *
 * @returns {Set<string>} 过程消息 id 集合
 */
export function computeProcessIds(messages) {
  const ids = new Set()
  if (!Array.isArray(messages)) return ids
  let nextIsProcess = false
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    const core =
      !!m && (m.type === 'thinking' || m.type === 'toolCall' || m.type === 'tool')
    const isProcess = core || (!!m && m.type === 'assistant' && nextIsProcess)
    if (isProcess && m && m.id != null) ids.add(m.id)
    nextIsProcess = isProcess
  }
  return ids
}

/** 该消息是否属于"过程"（要进 trace）。 */
export function isProcessMessage(msg, processIds) {
  if (!msg) return false
  if (msg.type === 'thinking') return true
  if (msg.type === 'toolCall' || msg.type === 'tool') return true
  if (msg.type === 'assistant' && processIds && processIds.has(msg.id)) return true
  return false
}

/* ---------------------------------------------------------------------------
 * 图标规则
 *
 * 截图里出现的图标：🐍（命令/脚本执行）、📄（脚本文件）、🔍（思考）、
 * ℹ️（过程叙述）、✓（校验通过）、🔧（其它工具，兜底）。
 * 其中 🔍/ℹ️ 的区分由消息类型决定（thinking → 🔍，叙述 → ℹ️），有据可依；
 * 工具类的细分★是猜测，按工具名映射，改这张表即可。
 * ------------------------------------------------------------------------ */
const TOOL_ICONS = {
  execute_shell_command: '🐍',
  runSkillScript: '🐍',
  load_skill: '🐍',
  readSkillFile: '📄',
  read_file: '📄',
  write_file: '📄',
  edit_file: '📄',
  renderDocx: '📄',
  renderDocxFromFile: '📄',
  renderDocxFromFiles: '📄',
  dispatch_report: '✓',
  verify_chart: '✓',
  verify_graph: '✓',
  list_files: '📁',
  search_file: '🔍',
  search_content: '🔍',
  web_search: '🔍',
  web_fetch: '🌐',
  send_file: '📎'
}

/** ★猜测：兜底图标。 */
const DEFAULT_TOOL_ICON = '🔧'
/** 过程叙述（虚线圆 SVG）。 */
const NOTE_ICON = 'ℹ️'
/** 思考内容：与中间叙述一致，用同一个虚线圆（对齐目标端 intermediate 行）。 */
const THINKING_ICON = NOTE_ICON

/** 取某条消息对应行的图标。 */
export function traceIconFor(msg) {
  if (!msg) return DEFAULT_TOOL_ICON
  if (msg.type === 'thinking') return THINKING_ICON
  if (msg.type === 'assistant') return NOTE_ICON
  const name = toolNameOf(msg)
  return TOOL_ICONS[name] || DEFAULT_TOOL_ICON
}

/** 尝试把 args 解析成对象；解析不了返回 null。 */
function parseArgs(args) {
  if (args == null || args === '') return null
  if (typeof args === 'object') return args
  if (typeof args !== 'string') return null
  try {
    const obj = JSON.parse(args)
    return obj && typeof obj === 'object' ? obj : null
  } catch (e) {
    return null
  }
}

/**
 * 工具的详情段：把 args 的各个字段按**原本的键顺序**摊成若干段，用 · 连接。
 *
 * 截图里的形态是 `weekly_agg.py · 从日数据聚合周收入与毛利 · data/weekly_agg.py`，
 * 看起来就是参数的字段值按原顺序排列，所以这里不做优先级重排。
 * 解析不出对象时退化为整串原文。
 *
 * 例外：args 带 `intent` 时**只**显示 intent（沿用旧工具卡片 getArgsSummary 的规则）。
 * 这类 args（如技能脚本调用 `{code, intent, language, skillName}`）里 intent 是唯一
 * 一句人话，其余是长代码与元数据；若按原序平摊，折叠行会被 code 占满、截断后只剩代码。
 */
export function traceSegmentsFor(msg) {
  if (!msg || !msg.args) return []
  const obj = parseArgs(msg.args)
  if (!obj) return [String(msg.args)]
  // 有 intent 属性时优先只取其内容作为详情段
  if (obj.intent != null && obj.intent !== '') {
    return [typeof obj.intent === 'string' ? obj.intent : String(JSON.stringify(obj.intent))]
  }
  const segs = []
  for (const key of Object.keys(obj)) {
    const v = obj[key]
    if (v == null || v === '') continue
    if (typeof v === 'object') {
      // 嵌套对象/数组压成一行短文本，太长就丢掉（那是展开详情该干的事）
      let s = ''
      try {
        s = JSON.stringify(v)
      } catch (e) {
        s = ''
      }
      if (s && s.length <= 60) segs.push(s)
      continue
    }
    segs.push(String(v))
    if (segs.length >= MAX_SEGMENTS) break
  }
  return segs
}

/**
 * ★猜测：行上的徽标数字。
 *
 * 截图里「按周聚合脚本」那行名称后面有一个 27，语义不明（可能是脚本行数 /
 * 影响行数 / 结果条数 / 耗时）。当前一律返回 null —— 不猜造数字，
 * 等你确认语义后在这里实现即可，行的结构已经支持。
 */
export function traceBadgeFor() {
  return null
}

/** 结果里是否带 MEDIA: 行（带则右侧是"可打开"箭头）。 */
export function mediaOf(msg) {
  if (!msg || typeof msg.result !== 'string') return ''
  const m = msg.result.match(/^MEDIA:(.+?)(?:\n|$)/m)
  return m ? m[1].trim() : ''
}

/**
 * 把一次运行的过程消息摊成行。
 *
 * 一条消息 → 一行（工具调用同理，一个工具一行），顺序即消息顺序。
 *
 * @param {Array} msgs 属于同一条 trace 的消息
 * @returns {Array} 行数组
 */
export function buildTraceRows(msgs, isLive) {
  if (!Array.isArray(msgs)) return []
  const rows = []
  msgs.forEach((msg, i) => {
    if (!msg) return
    const key = 'row-' + (msg.id || i)
    const status = traceStatusFor(msg, isLive)
    if (msg.type === 'toolCall' || msg.type === 'tool') {
      const media = mediaOf(msg)
      const meta = msg.meta || {}
      const toolName = toolNameOf(msg)
      const parentId = msg.parentId || meta.parentId || meta.parentToolCallId || ''
      const depth = typeof msg.depth === 'number' ? msg.depth : parentId ? 1 : 0
      // 中间叙述（thought）单独成行，与工具行并列：对齐目标端的 intermediate 行
      const thought = thoughtOf(msg)
      if (thought) {
        rows.push({
          key: key + '-thought',
          kind: 'note',
          msg: null,
          status,
          toolName: '',
          name: '',
          intent: '',
          paramsSummary: '',
          diffStats: null,
          fileBadge: '',
          parentId,
          depth,
          isSubagent: false,
          openKind: 'text',
          media: '',
          icon: NOTE_ICON,
          badge: null,
          segments: [],
          text: thought
        })
      }
      rows.push({
        key,
        kind: 'tool',
        msg,
        status,
        toolName,
        name: displayNameOf(msg),
        intent: intentOf(msg),
        paramsSummary: paramsSummaryOf(msg),
        diffStats: traceDiffStatsFor(msg),
        fileBadge: fileBadgeOf(msg),
        parentId,
        depth,
        isSubagent: SUBAGENT_TOOL_NAMES.indexOf(normalizeToolName(toolName)) !== -1,
        openKind: media ? 'link' : 'detail',
        media,
        // 工具品牌（客户端 toolDisplayMeta.iconDataUrl + formatToolDisplay 的名称前缀），
        // 无品牌时回落到状态图标
        brand: brandFor(msg),
        // 兼容旧字段（CosmoClawTurnDetail 仍按 icon/segments/text 渲染）
        icon: traceIconFor(msg),
        badge: traceBadgeFor(msg),
        segments: traceSegmentsFor(msg),
        text: ''
      })
      return
    }
    const raw = typeof msg.content === 'string' ? msg.content : ''
    rows.push({
      key,
      kind: msg.type === 'thinking' ? 'thinking' : 'note',
      msg,
      status,
      toolName: '',
      name: '',
      intent: '',
      paramsSummary: '',
      diffStats: null,
      fileBadge: '',
      parentId: '',
      depth: 0,
      isSubagent: false,
      openKind: 'text',
      media: '',
      icon: traceIconFor(msg),
      badge: null,
      segments: [],
      text: stripProcessMarker(raw)
    })
  })
  return rows
}

/* ---------------------------------------------------------------------------
 * 目标式行信息（对齐客户端 TurnCard 的 ActivityRow）
 *
 * 目标行的结构是：状态图标 + 名称 + diff 徽标 + 文件徽标 + ·意图 + ·参数摘要。
 * 目标端这些字段来自后端注入的 toolDisplayMeta / intent；本项目的 WS 只下发
 * toolName / args / result / meta，因此这里按「args 线索 → 中文名映射」逐级降级：
 * 缺哪个字段就不渲染哪一段，不留空壳。
 * ------------------------------------------------------------------------ */

/** 参数里属于元数据 / 长文本的键，不参与参数摘要。 */
const META_ARG_KEYS = [
  '_intent',
  'intent',
  '_displayName',
  'displayName',
  'description',
  '_description',
  'code'
]

/** 只显示文件路径的工具（对齐目标端 Edit / Write 的规则）。 */
const FILE_ONLY_TOOLS = [
  'edit_file',
  'editFile',
  'edit',
  'multiedit',
  'multiEdit',
  'write_file',
  'writeFile',
  'write'
]

/** 子代理类工具（目标端 PARENT_TASK_TOOLS）：用于右侧"可分组"判定。 */
const SUBAGENT_TOOL_NAMES = [
  'task',
  'agent',
  'delegateToAgent',
  'delegateParallel',
  'delegateAsync'
]

/** 会携带 Todo 列表的工具。 */
const TODO_TOOL_NAMES = ['TodoWrite', 'todo_write', 'write_todos', 'progress_update']

/** 剥掉 mcp 前缀后的工具名：`mcp__session__call_llm` → `call_llm`。 */
export function normalizeToolName(name) {
  const raw = String(name || '')
  const m = raw.match(/^mcp__[^_]+__(.+)$/)
  return m ? m[1] : raw
}

/** 脚本语言 → 命令名（对齐客户端：先把 Bash 命令解析成 CLI，再取品牌图标）。 */
const SCRIPT_LANGUAGE_COMMAND = {
  python: 'python',
  python3: 'python',
  py: 'python',
  node: 'node',
  nodejs: 'node',
  javascript: 'node',
  js: 'node',
  typescript: 'node',
  ts: 'node'
}

/**
 * 工具行的品牌图标（对齐客户端 toolDisplayMeta.iconDataUrl）。
 *
 * 客户端规则：后端给工具注入了图标就用注入的（CLI 品牌 / 技能 / 数据源 / 浏览器），
 * 没注入的工具回落到状态图标（工具调用 / 步骤完成）。
 * 本项目 WS 不下发图标，所以在本地按同样规则推断：
 *   - 后端若下发 meta.iconDataUrl / meta.iconUrl → 直接使用；
 *   - browser_use → chrome 图标（客户端 BROWSER_TOOL_ICON_FILENAME）；
 *   - execute_shell_command → 从 args.command 解析 CLI 品牌；
 *   - runSkillScript → 从 args.command / args.language 解析脚本语言对应的品牌。
 * 其余工具（读文件、写文件、技能、搜索…）在客户端同样没有专属图标，返回 null。
 */
export function brandFor(msg) {
  if (!msg) return null
  const meta = msg.meta || {}
  const provided = meta.iconDataUrl || meta.iconUrl
  if (typeof provided === 'string' && /^(data:|https?:|\/|\.)/.test(provided)) {
    return { icon: provided, label: '' }
  }

  const name = normalizeToolName(toolNameOf(msg))
  const args = parseArgs(msg.args) || {}

  // 浏览器工具：客户端只给 chrome 图标，不额外加名称前缀
  if (name === 'browser_use') return { icon: BROWSER_TOOL_BRAND.icon, label: '' }

  // 客户端 formatToolDisplay：Bash 命中 CLI 品牌时名称渲染成「Python: <动作>」
  if (name === 'execute_shell_command' || name === 'runSkillScript') {
    const command =
      name === 'execute_shell_command'
        ? args.command
        : args.command || SCRIPT_LANGUAGE_COMMAND[String(args.language || '').toLowerCase()]
    const brand = resolveCommandBrand(command)
    if (!brand) return null
    return { icon: brand.icon, label: brand.displayName + ':' }
  }

  return null
}

/** 取路径最后一段作为文件名徽标。 */
function baseName(p) {
  const s = String(p || '')
  const idx = Math.max(s.lastIndexOf('/'), s.lastIndexOf('\\'))
  return idx === -1 ? s : s.slice(idx + 1)
}

/** 行首显示名：args / meta 里的语义名优先，其次中文映射，最后原名。 */
function displayNameOf(msg) {
  const obj = parseArgs(msg.args) || {}
  const meta = msg.meta || {}
  const alias = obj._displayName || obj.displayName || meta.displayName
  if (alias) return String(alias)
  const raw = String(toolNameOf(msg) || '')
  const m = raw.match(/^mcp__[^_]+__(.+)$/)
  if (m) return m[1].replace(/_/g, ' ')
  return getToolDisplayName(raw)
}

/** 意图 / 描述：目标端展示在名称后面的那句人话。 */
function intentOf(msg) {
  const obj = parseArgs(msg.args) || {}
  const meta = msg.meta || {}
  const v = obj._intent || obj.intent || obj.description || meta.intent
  return v ? String(v).replace(/\s+/g, ' ').trim() : ''
}

/**
 * 工具自带的中间叙述（推理 / thought）。
 * 目标端这类内容以独立的 intermediate 行呈现，可点击行内展开、悬停箭头开全屏；
 * 本项目 WS 把它挂在工具消息的 thought / meta.thought 上，这里拆出来单独成行。
 */
function thoughtOf(msg) {
  if (!msg) return ''
  const meta = msg.meta && typeof msg.meta === 'object' ? msg.meta : {}
  const v = msg.thought || msg.reasoning || meta.thought || meta.reasoning
  if (typeof v !== 'string' || !v.trim()) return ''
  return stripProcessMarker(v.trim())
}

/** 参数摘要：跳过元数据键，最多两段（对齐目标 formatToolInput）。 */
function paramsSummaryOf(msg) {
  const obj = parseArgs(msg.args)
  if (!obj) return ''
  const name = normalizeToolName(toolNameOf(msg))
  if (FILE_ONLY_TOOLS.indexOf(name) !== -1) {
    const p = obj.file_path || obj.path
    return p ? String(p) : ''
  }
  if (Array.isArray(obj.changes)) {
    const first = obj.changes[0] || {}
    return first.path ? String(first.path) : ''
  }
  const parts = []
  for (const key of Object.keys(obj)) {
    if (META_ARG_KEYS.indexOf(key) !== -1) continue
    const v = obj[key]
    if (v == null || v === '') continue
    let s = typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : JSON.stringify(v)
    if (!s) continue
    if (s.length > 80) s = s.slice(0, 80) + '…'
    parts.push(s)
    if (parts.length >= 2) break
  }
  return parts.join(' ')
}

/** 文件徽标：file_path / path / changes[0].path 的 basename。 */
function fileBadgeOf(msg) {
  const obj = parseArgs(msg.args)
  if (!obj) return ''
  const p =
    obj.file_path ||
    obj.path ||
    (Array.isArray(obj.changes) && obj.changes[0] ? obj.changes[0].path : '')
  return p ? baseName(p) : ''
}

/** 按行做 LCS 差异统计；超过 400 行直接放弃（徽标不值得为它算这么久）。 */
function diffLineStats(oldStr, newStr) {
  const a = oldStr ? String(oldStr).split('\n') : []
  const b = newStr ? String(newStr).split('\n') : []
  if (a.length > 400 || b.length > 400) return null
  const n = b.length
  let prev = new Array(n + 1).fill(0)
  for (let i = 1; i <= a.length; i++) {
    const cur = new Array(n + 1).fill(0)
    for (let j = 1; j <= n; j++) {
      cur[j] =
        a[i - 1] === b[j - 1] ? prev[j - 1] + 1 : Math.max(prev[j], cur[j - 1])
    }
    prev = cur
  }
  const lcs = prev[n]
  return { deletions: a.length - lcs, additions: n - lcs }
}

/** 编辑 / 写入类工具的增删行数（对齐目标 computeEditWriteDiffStats）。 */
export function traceDiffStatsFor(msg) {
  const obj = parseArgs(msg && msg.args)
  if (!obj) return null
  const name = normalizeToolName(toolNameOf(msg))
  if (name === 'edit_file' || name === 'editFile' || name === 'edit' || name === 'multiedit' || name === 'multiEdit') {
    const stats = diffLineStats(obj.old_string || '', obj.new_string || '')
    return stats && (stats.additions || stats.deletions) ? stats : null
  }
  if (name === 'write_file' || name === 'writeFile' || name === 'write') {
    const content = obj.content
    if (typeof content !== 'string' || !content) return null
    return { additions: content.replace(/\n$/, '').split('\n').length, deletions: 0 }
  }
  return null
}

/** 行状态：pending / running / completed / error（对齐目标 ActivityStatus）。 */
export function traceStatusFor(msg, isLive) {
  if (!msg) return 'completed'
  if (msg.isError) return 'error'
  if (msg.type === 'toolCall' || msg.type === 'tool') {
    if (msg.phase === 'result') return 'completed'
    if (msg.phase === 'start' || msg.phase === 'update') return 'running'
    return msg.isStreaming ? 'running' : 'completed'
  }
  if (msg.isStreaming && isLive) return 'running'
  return 'completed'
}

/**
 * 取本轮最新的 Todo 列表（目标端 TodoWrite 的可视化）。
 * 没有相关工具、或数据里没有 todos 时返回空数组 —— 组件据此整块不渲染。
 */
export function extractTodos(msgs) {
  if (!Array.isArray(msgs)) return []
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i]
    if (!m || (m.type !== 'toolCall' && m.type !== 'tool')) continue
    if (TODO_TOOL_NAMES.indexOf(normalizeToolName(toolNameOf(m))) === -1) continue
    const obj = parseArgs(m.args)
    const list = obj && (obj.todos || obj.items)
    if (!Array.isArray(list) || !list.length) continue
    return list
      .map((t, idx) => {
        if (!t) return null
        let status = t.status || 'pending'
        if (status === 'in-progress') status = 'in_progress'
        if (status === 'done') status = 'completed'
        const content = t.content || t.text || t.title || t.subject || ''
        return { key: 'todo-' + idx, content: String(content), status }
      })
      .filter(Boolean)
  }
  return []
}

/**
 * 表头文案，逐条对齐目标端 `getPreviewText`（TurnCard.tsx 654-721）：
 *
 *  1. 本轮意图：最后一条带 intent 的行（运行中 / 结束后都用它，始终跟最新一次调用走）
 *  2. 运行中且正文已开始输出 → 「回复中...」
 *  3. 运行中 → 最后一条叙述/思考的尾部（超过 200 字取尾部并加「…」）
 *  4. 结束 → 「步骤已完成」，有失败行时追加「 · N 步未完成」
 *  5. 其余（含正在跑工具名的情况）→ 「工具调用中」
 */
export function traceHeaderLabel(rows, isLive, answerStarted) {
  if (!Array.isArray(rows) || !rows.length) return '过程'

  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i].intent) return rows[i].intent
  }

  if (isLive && answerStarted) return '回复中...'

  if (isLive) {
    for (let i = rows.length - 1; i >= 0; i--) {
      const row = rows[i]
      if (row.kind === 'tool' || !row.text) continue
      const text = String(row.text).trim()
      return text.length > 200 ? '…' + text.slice(text.length - 200) : text
    }
  }

  if (!isLive) {
    const errorCount = rows.filter((row) => row.status === 'error').length
    return errorCount > 0 ? `步骤已完成 · ${errorCount} 步未完成` : '步骤已完成'
  }

  return '工具调用中'
}

/**
 * trace 表头右侧的步数。截图里是一个裸数字（80），所以这里返回行数。
 */
export function traceStepCount(rows) {
  return Array.isArray(rows) ? rows.length : 0
}
